import { inject, InjectionToken } from '@angular/core';
import { Router, UrlTree, CanActivateFn, CanActivateChildFn } from '@angular/router';

import { iif, Observable, of, withLatestFrom } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { AuthenticationFacade } from '@cash/shared/authentication';

import { AuthorizationFacade } from '../facade';
import { NiloPermissions } from '../constants';

class AuthorizedGuardFactory {
  private constructor(
    private authorizationFacade: AuthorizationFacade,
    private router: Router,
    private permissions: NiloPermissions[],
    private authenticationFacade: AuthenticationFacade,
  ) {}

  public static buildGuardFactory(permissions: NiloPermissions[]): AuthorizedGuardFactory {
    const authorizationFacade = inject(AuthorizationFacade);
    const router = inject(Router);
    const authenticationFacade = inject(AuthenticationFacade);
    return new AuthorizedGuardFactory(
      authorizationFacade,
      router,
      permissions,
      authenticationFacade,
    );
  }

  canActivate(): Observable<boolean | UrlTree> {
    return this.isAuthorized();
  }

  canActivateChild(): Observable<boolean | UrlTree> {
    return this.isAuthorized();
  }

  isAuthorized(): Observable<boolean | UrlTree> {
    return this.authorizationFacade.hasPermissions$(this.permissions).pipe(
      withLatestFrom(
        this.authenticationFacade.hasValidAccessToken$,
        this.authenticationFacade.getExpirationDate$,
      ),
      mergeMap(([isAuthorized, hasToken, expirationDate]) => {
        const validToken = expirationDate > new Date();
        if (!hasToken || !validToken) {
          this.authenticationFacade.clearStorage();
        }
        return iif(
          () => isAuthorized && hasToken && validToken,
          of(true),
          of(this.router.parseUrl(!hasToken || !validToken ? 'logout' : '403')),
        );
      }),
    );
  }
}

export class CashAuthorizedGuard {
  static withPermissions = (permissions: NiloPermissions[]) =>
    new InjectionToken<
      | {
          canActivate: CanActivateFn;
        }
      | {
          canActivateChild: CanActivateChildFn;
        }
    >('AuthorizedGuard', {
      factory: () => AuthorizedGuardFactory.buildGuardFactory(permissions),
    });
}
